'use client';

import './styles/@theme';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { OptInButton } from '../opt-in-button/opt-in-button';
import type { SiteWideJackpotMultiplierBoostItem } from '@/types/aggregation/site-wide-jackpot';
import { mapMultipliers } from '@/app/components/site-wide-jackpot/hooks/use-prismic-jackpot-info';
import { useStore } from '@/store/store';
import { AnimatedTopBar } from '../animated-top-bar/animated-top-bar';
import { OptInMultiplier } from '../opt-in-multiplier/opt-in-multiplier';
import { useAccountUm } from '@/hooks/use-account-um';
import { useMemo } from 'react';
import { POPUPS } from '@/app/components/popup/constants';
import type { SupportLink } from '@/types/layout/link-view-model';

export type ConfirmationZoneProps = {
    scrolledView?: boolean;
    multipliers: SiteWideJackpotMultiplierBoostItem[];
    currentMultiplier: number;
    description: string;
    logoUrl: string;
    shortRules: string;
    handleRulesClick: () => void;
    jackpotId: string;
    support?: SupportLink;
};

export const ConfirmationZone = ({
    scrolledView,
    multipliers,
    currentMultiplier,
    description,
    logoUrl,
    shortRules,
    handleRulesClick,
    jackpotId,
    support
}: ConfirmationZoneProps) => {
    const { triggerLogin } = useAccountUm();
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const { queuePopup, closePopup } = useStore((s) => s.popup);

    const params = useMemo(() => {
        const jackpotMultipliers = mapMultipliers(multipliers);

        return {
            jackpotLogoUrl: logoUrl,
            shortRules,
            multipliers: jackpotMultipliers,
            jackpotId,
            support,
            handleJackpotRules: () => {
                handleRulesClick();
                closePopup();
            }
        };
    }, [closePopup, logoUrl, handleRulesClick, multipliers, shortRules, jackpotId, support]);

    const handlePopup = (value: boolean) => {
        if (!value) {
            return closePopup();
        }

        return queuePopup({
            type: POPUPS.SWJ_OPT_IN,
            params: { swjParams: params }
        });
    };

    const handleOptInClick = () => {
        if (!isLoggedIn) {
            triggerLogin();
        } else {
            handlePopup(true);
        }
    };

    return (
        <div
            id="site-wide-jackpot-confirmation-zone"
            className="confirmation-zone"
            data-test-id="site-wide-jackpot-confirmation-zone">
            {currentMultiplier === 0 ? (
                scrolledView ? (
                    <div
                        className="opted-out-wrapper"
                        role="none"
                        onClick={() => handleOptInClick()}
                        onKeyDown={() => handleOptInClick()}>
                        <AnimatedTopBar multipliers={multipliers} multiplier={currentMultiplier} />
                        <OptInButton handleOptInClick={() => handleOptInClick()} />
                    </div>
                ) : (
                    <>
                        <p className="confirmation-zone-description">{description}</p>
                        <OptInButton handleOptInClick={() => handleOptInClick()} />
                    </>
                )
            ) : (
                multipliers
                    .filter((i) => i.multiplier === currentMultiplier)
                    .map((optInMultiplier) => (
                        <OptInMultiplier
                            scrolledView={scrolledView ?? false}
                            key={optInMultiplier.multiplier}
                            multipliers={multipliers}
                            multiplier={optInMultiplier.multiplier ?? 0}
                            wageredAmount={optInMultiplier.wagered_amount ?? 0}
                            shortWagerLabel={optInMultiplier.short_wager_label?.[0]?.text}
                            handleOptOut={() => handlePopup(true)}
                        />
                    ))
            )}
        </div>
    );
};
